import React, { useEffect, useState } from "react";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import "./authExperience.css";
import { getAuthSwitchPath } from "../identity/identityClient";

const EXPERIENCE_ROOT = "/packages/experiences/identity/arrival/login/immersive-center";
const LOCKUP = "/images/clikkle/brand/logos/clikkle-arrival-lockup.png";

export default function AuthExperience({
  onContinue,
  mode = "login",
  productName,
  productBrandPath,
  productHomeUrl = "https://clikkle.com",
  loginPath = "/login",
  signupPath = "/signup",
}) {
  const [hidden, setHidden] = useState(document.hidden);
  const [stage, setStage] = useState("arrival");
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    setStage("arrival");
    setRedirecting(false);
  }, [mode]);

  const arrivalProductName = String(productName || "Store").replace(/^Clikkle\s+/i, "");
  const arrivalActionLabel = mode === "signup" ? `Create your ${arrivalProductName} account` : `Enter ${arrivalProductName}`;
  const gatewayTitle = mode === "signup" ? `Create your ${productName} account` : `Continue to ${productName}`;
  const gatewayCopy = mode === "signup"
    ? `Create your identity through Clikkle Identity, then return securely to ${productName}.`
    : `Sign in through Clikkle Identity, then return securely to ${productName}.`;
  const gatewayAction = mode === "signup" ? "Create account securely" : "Continue securely";
  // The shared experience owns preservation too. Callers may provide a custom
  // route, but the standard Store login/signup switch must never discard a
  // pending commerce transaction.
  const resolvedLoginPath = loginPath === "/login" ? getAuthSwitchPath("login") : loginPath;
  const resolvedSignupPath = signupPath === "/signup" ? getAuthSwitchPath("signup") : signupPath;

  const openGateway = () => setStage("gateway");
  const returnToArrival = () => setStage("arrival");
  const continueToIdentity = () => {
    if (redirecting) return;
    setRedirecting(true);
    onContinue?.();
  };

  return (
    <main className={`authArrival${hidden ? " is-paused" : ""}`} data-auth-stage={stage}>
      <picture className="authArrival__scene" aria-hidden="true">
        <source media="(max-width: 700px)" srcSet={`${EXPERIENCE_ROOT}/mobile.webp`} />
        <img src={`${EXPERIENCE_ROOT}/desktop.webp`} alt="" decoding="async" fetchPriority="high" />
      </picture>

      {stage === "arrival" ? (
        <section className="authArrival__content authArrival__content--arrival" aria-labelledby="auth-arrival-title">
          <h1 id="auth-arrival-title" className="authArrival__srOnly">{arrivalActionLabel}</h1>
          <a className="authArrival__brandLink" href="https://clikkle.com" aria-label="Visit Clikkle.com">
            <img className="authArrival__lockup" src={LOCKUP} alt="Clikkle. Connect. Create. Accelerate." />
          </a>
          <p className="authArrival__subtitle">Continue into your digital ecosystem.</p>
          <div className="authArrival__action">
            <button type="button" className="authArrival__button" onClick={openGateway} aria-label={arrivalActionLabel}>
              <ArrowForwardRoundedIcon aria-hidden="true" />
            </button>
            <span>{arrivalActionLabel}</span>
          </div>
          <div className="authArrival__trust" aria-label="Secure, private, always yours">
            <LockOutlinedIcon aria-hidden="true" />
            <span>Secure</span><span>•</span><span>Private</span><span>•</span><span>Always Yours</span>
          </div>
        </section>
      ) : (
        <section className="authArrival__gatewayWrap" aria-labelledby="auth-gateway-title">
          <div className="authArrival__gateway">
            <button type="button" className="authArrival__back" onClick={returnToArrival} aria-label="Back to arrival screen">
              <ArrowBackRoundedIcon aria-hidden="true" />
            </button>

            <a className="authArrival__productBrand" href={productHomeUrl} aria-label={`Visit ${productName}`}>
              <img src={productBrandPath} alt={`${productName} logo`} />
            </a>
            <h1 id="auth-gateway-title">{gatewayTitle}</h1>
            <p>{gatewayCopy}</p>

            <button type="button" className="authArrival__continue" onClick={continueToIdentity} disabled={redirecting}>
              {redirecting ? "Opening Clikkle Identity…" : gatewayAction}
            </button>

            <div className="authArrival__alternate">
              {mode === "signup" ? (
                <>Already have a Clikkle ID? <a href={resolvedLoginPath}>Sign in</a></>
              ) : (
                <>New to Clikkle? <a href={resolvedSignupPath}>Create account</a></>
              )}
            </div>

            <span className="authArrival__protocol">Protected by Clikkle Identity · Protocol v2</span>
          </div>
        </section>
      )}
    </main>
  );
}
